import type {
  FilingSendStatusDto,
  FilingSendResultDto,
  SendToStateRequest,
  ConfirmDestinationRequest,
  FilingDestinationConfirmedResponse,
} from '@signalvote/shared';
import { skip, type CurrentRead } from '@signalvote/data';
import { apiJson, apiResponse, type ApiErrorBody } from './apiFetch';
import { campaignHeaders, defineQuery, defineWrite } from '../../lib/data';

/**
 * Issue #861 — the treasurer "authorize and send" surface over the send-and-confirm backend. Three
 * hooks: the derived "Sent" + pre-authorize destination lens (`useFilingSendStatus`), the app-send
 * mutation (`useSendFilingToState`), and the one-time county-destination confirmation
 * (`useConfirmFilingDestination`). The report's own status never changes on send — "sent" is a lens
 * over the send record, which the send's write set refreshes.
 */

/**
 * A send / confirm failure that preserves the backend's stable error `code` (AlreadySent,
 * DestinationNotConfirmed, SendFailed, …) so the surface can branch to the right copy or step
 * (e.g. show the confirm-destination step on `DestinationNotConfirmed`) rather than parse a message.
 */
export class FilingSendActionError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'FilingSendActionError';
  }
}

// The error body can be the JSON literal `null`, hence the optional chaining.
function actionError(fallback: string) {
  return (body: ApiErrorBody | null, response: Response) =>
    new FilingSendActionError(
      (body?.code as string) ?? 'Unknown',
      (body?.error as string) ?? (body?.message as string) ?? fallback,
      response.status,
    );
}

async function throwFromResponse(response: Response, fallback: string): Promise<never> {
  const body = await response.json().catch(() => null);
  throw actionError(fallback)(body, response);
}

/**
 * The derived "Sent" lens for a report + the pre-authorize destination lens (resolved destination
 * verbatim, county-confirmation need). staleTime 0 so a fresh send, a delivery webhook, or a
 * destination confirmation shows on the authorize surface without a manual refresh.
 */
const filingSendStatus = defineQuery({
  key: (reportId: string) => ['finance', 'reports', 'send-status', reportId],
  reads: 'GET /api/finance/reports/{id}/send-status',
  staleTime: 0,
  fetch: (reportId, { signal, campaignId }) =>
    apiJson<FilingSendStatusDto>(`/api/finance/reports/${reportId}/send-status`, {
      signal,
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch filing send status',
    }),
});

export function useFilingSendStatus(reportId: string | null): CurrentRead<FilingSendStatusDto> {
  return filingSendStatus(reportId ?? skip);
}

/** The hook input: the report to send, plus the shared API body (`SendToStateRequest`). */
export type SendToStateVariables = { reportId: string } & SendToStateRequest;

/**
 * Email the report's generated filing to its state destination on the campaign's behalf. There is no
 * concurrency token: the report row is never changed on send ("sent" is a derived lens). The send's
 * write set names the send record, which refreshes the send status (and a package the send built).
 */
const useSendFilingToStateWrite = defineWrite({
  send: ({ reportId, ...body }: SendToStateVariables): Promise<FilingSendResultDto> => {
    const payload: SendToStateRequest = body;
    return apiJson<FilingSendResultDto>(`/api/finance/reports/${reportId}/send-to-state`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: payload,
      error: actionError('Failed to send the filing'),
    });
  },
});

export function useSendFilingToState() {
  const { mutateAsync } = useSendFilingToStateWrite();
  return { sendToState: mutateAsync };
}

function readDispositionFileName(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (star?.[1]) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
  const plain = /filename=("?)([^";]+)\1/i.exec(disposition);
  if (plain?.[2]) return plain[2].trim();
  return fallback;
}

/**
 * Generate the SIGNED artifact and return its bytes for the download-and-file-it-yourself escape hatch
 * (issue #861), so the downloaded document carries the signature the treasurer drew. Posts the drawn
 * signature to the (non-finalizing) generate endpoint, which stamps + persists the signed package.
 */
const useDownloadSignedFilingWrite = defineWrite({
  send: async ({
    reportId,
    signatureImageBase64,
  }: {
    reportId: string;
    signatureImageBase64: string;
  }): Promise<{ fileBlob: Blob; fileName: string }> => {
    const response = await apiResponse(`/api/finance/reports/${reportId}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: { signatureImageBase64 },
    });
    if (!response.ok) await throwFromResponse(response, 'Failed to generate the signed filing');
    const fileBlob = await response.blob();
    const fileName = readDispositionFileName(
      response.headers.get('Content-Disposition'),
      `report-${reportId}-signed.pdf`,
    );
    return { fileBlob, fileName };
  },
});

export function useDownloadSignedFiling() {
  const { mutateAsync } = useDownloadSignedFilingWrite();
  return { downloadSignedFiling: mutateAsync };
}

/** The hook input: the report, plus the shared API body (`ConfirmDestinationRequest`). */
export type ConfirmDestinationVariables = { reportId: string } & ConfirmDestinationRequest;

/**
 * Record the treasurer's one-time confirmation of a county filing destination (append-only decision),
 * so `destinationConfirmed` flips and the authorize step can proceed to the send. The confirmation is
 * an audit-log row, which the write set names, so the send status refreshes from it.
 */
const useConfirmFilingDestinationWrite = defineWrite({
  send: ({ reportId, destination }: ConfirmDestinationVariables) =>
    apiJson<FilingDestinationConfirmedResponse>(`/api/finance/reports/${reportId}/confirm-destination`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      json: { destination },
      error: actionError('Failed to confirm the filing destination'),
    }),
});

export function useConfirmFilingDestination() {
  const { mutateAsync } = useConfirmFilingDestinationWrite();
  return { confirmDestination: mutateAsync };
}
